'use client'

import React, { useMemo } from 'react'

import { M_BUTTON_VARIANT_ENUM, MButton, THEME_COLORS_ENUM } from '../../../../components/button'
import { MProgramCard } from '../../../../components/program-card'
import { cn } from '../../../../utils/cn'
import { MODULES_HREF } from '../_constants'

import type { IProgram } from '../../../../components/program-card'
import type { IProgramSection } from '../_types/types'

export interface IAAnnualProgramPreviewProgramProps {
	id?: string
	className?: string
	style?: React.CSSProperties
	link?: React.ElementType<React.AnchorHTMLAttributes<HTMLAnchorElement>>
	programSection: IProgramSection
	programWithOrderUrl: IProgram
	currentProgram: IProgram
	orderUrl: string | undefined
	orderButtonLabelFormatted: { desktop: string; mobile: string }
	isFooterVisible: boolean
	sectionRef: React.RefObject<HTMLElement | null>
	renderSwitcher: () => React.ReactNode
	/** When false, program switcher buttons are hidden in this section. Default: true when multiple programs. */
	showProgramsSwitch: boolean
}

export const AAnnualProgramPreviewProgram: React.FC<IAAnnualProgramPreviewProgramProps> = ({
	id,
	className,
	style,
	link,
	programSection,
	programWithOrderUrl,
	currentProgram,
	orderUrl,
	orderButtonLabelFormatted,
	isFooterVisible,
	sectionRef,
	renderSwitcher,
	showProgramsSwitch
}) => {
	const headerOrderButtonLabel = programSection.headerOrderButtonLabel ?? 'Objednať ročný program'
	const orderButtonLabel = programSection.orderButtonLabel ?? 'Objednať program ({price})'
	const showAddModulesButton = programSection.showAddModulesButton ?? false

	const headerButtons: React.ReactNode[] | undefined = useMemo(
		() => [
			...(orderUrl
				? [
						<MButton
							key='order'
							href={orderUrl}
							linkComponent={link}
							color={currentProgram.color as THEME_COLORS_ENUM}
							variant={M_BUTTON_VARIANT_ENUM.TRANSPARENT}
						>
							{headerOrderButtonLabel}
						</MButton>
					]
				: []),
			...(showAddModulesButton
				? [
						<MButton
							key='modules'
							href={MODULES_HREF}
							linkComponent={link}
							color={currentProgram.color as THEME_COLORS_ENUM}
							variant={M_BUTTON_VARIANT_ENUM.TRANSPARENT_OUTLINE}
							data-scroll-to={MODULES_HREF.replace('#', '')}
						>
							Pridať voliteľné moduly
						</MButton>
					]
				: [])
		],
		[
			orderUrl,
			link,
			currentProgram.color,
			headerOrderButtonLabel,
			showAddModulesButton
		]
	)

	return (
		<section
			ref={sectionRef}
			id={id}
			className={cn('m-annual-program-preview', className)}
			style={style}
		>
			{showProgramsSwitch && renderSwitcher()}

			<MProgramCard
				key={currentProgram.id}
				program={programWithOrderUrl}
				forceExpand
				showOrderArrow={false}
				headerButtons={headerButtons}
				link={link}
				orderButtonLabel={orderButtonLabel}
				eyebrow='Ročný program'
				modulesHref={showAddModulesButton ? MODULES_HREF : undefined}
			/>

			{/* Mobile Sticky Footer */}
			<div
				className={cn(
					'm-annual-program-preview__mobile-footer',
					isFooterVisible
						? 'm-annual-program-preview__mobile-footer--visible'
						: 'm-annual-program-preview__mobile-footer--hidden'
				)}
			>
				<div className='m-annual-program-preview__mobile-footer-inner'>
					{orderUrl && (
						<MButton
							href={orderUrl}
							linkComponent={link}
							color={currentProgram.color as THEME_COLORS_ENUM}
							variant={M_BUTTON_VARIANT_ENUM.SOLID}
							className='m-annual-program-preview__mobile-footer-btn'
						>
							<span
								className='desktop'
								dangerouslySetInnerHTML={{
									__html: orderButtonLabelFormatted.desktop
								}}
							/>
							<span
								className='mobile'
								dangerouslySetInnerHTML={{
									__html: orderButtonLabelFormatted.mobile
								}}
							/>
						</MButton>
					)}
					{showAddModulesButton && (
						<MButton
							href={MODULES_HREF}
							linkComponent={link}
							color={currentProgram.color as THEME_COLORS_ENUM}
							variant={M_BUTTON_VARIANT_ENUM.OUTLINE}
							className='m-annual-program-preview__mobile-footer-btn'
							data-scroll-to={MODULES_HREF.replace('#', '')}
						>
							Pridať voliteľné moduly
						</MButton>
					)}
				</div>
			</div>
		</section>
	)
}

AAnnualProgramPreviewProgram.displayName = 'AAnnualProgramPreviewProgram'
